import { Facebook, Instagram, MapPin, Phone, Mail, Clock } from "lucide-react";
import { Link } from "react-router-dom";
import { Logo } from "./Logo";

const footerLinks = {
  flooring: [
    { label: "Hardwood", href: "/products/hardwood" },
    { label: "Vinyl", href: "/products/vinyl" },
    { label: "Carpet", href: "/products/carpet" },
    { label: "Tile", href: "/products/tile" },
    { label: "Laminate", href: "/products/laminate" },
  ],
  company: [
    { label: "About Us", href: "/about-us" },
    { label: "Blog", href: "/blog" },
    { label: "Contact", href: "/contact" },
    { label: "Free Estimate", href: "/free-estimate" },
  ],
};

const locations = [
  {
    name: "South Charlotte Showroom",
    address: "9716-C Rea Road, Charlotte, NC 28277",
    phone: "(704) 419-8897",
  },
  {
    name: "Lake Norman Showroom",
    address: "9606-A Bailey Rd, Cornelius, NC 28031",
    phone: "(803) 590-8182",
  },
  {
    name: "Warehouse",
    address: "801-C Atando Ave, Charlotte, NC 28206",
    phone: "(704) 480-3855",
  },
];

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-secondary text-secondary-foreground" role="contentinfo">
      {/* Main Footer */}
      <div className="container-wide py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 lg:gap-12">
          {/* Logo & Contact */}
          <div className="lg:col-span-2">
            <Logo variant="light" className="mb-6" />
            <p className="text-secondary-foreground/85 text-sm mb-6 max-w-xs">
              Charlotte's premier flooring company. Premium floors, expert installation, 
              all-inclusive pricing.
            </p>
            
            {/* Hours */}
            <div className="flex items-start gap-2 mb-4">
              <Clock className="h-4 w-4 mt-0.5 text-primary" />
              <div className="text-sm text-secondary-foreground/85">
                <p>Mon-Fri: 10AM-6PM</p>
                <p>Sat: 10AM-4PM</p>
                <p>Sun: Closed</p>
              </div>
            </div>

            <div className="space-y-3">
              <a
                href="tel:+17043892166"
                className="flex items-center gap-2 text-sm text-secondary-foreground/85 hover:text-primary transition-colors"
              >
                <Phone className="h-4 w-4" />
                (704) 389-2166
              </a>
              <a
                href="mailto:info@mynewfloor.com"
                className="flex items-center gap-2 text-sm text-secondary-foreground/85 hover:text-primary transition-colors"
              >
                <Mail className="h-4 w-4" />
                info@mynewfloor.com
              </a>
            </div>
            
            {/* Social */}
            <div className="flex gap-4 mt-6">
              <a
                href="https://www.facebook.com/mynewfloorcom"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-lg bg-secondary-foreground/10 flex items-center justify-center hover:bg-primary transition-colors"
                aria-label="Follow us on Facebook"
              >
                <Facebook className="h-5 w-5" />
              </a>
              <a
                href="https://www.instagram.com/mynewfloorcom/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-lg bg-secondary-foreground/10 flex items-center justify-center hover:bg-primary transition-colors"
                aria-label="Follow us on Instagram"
              >
                <Instagram className="h-5 w-5" />
              </a>
            </div>
          </div>

          {/* Products */}
          <div>
            <h3 className="font-semibold text-secondary-foreground mb-4">Products</h3>
            <ul className="space-y-2">
              {footerLinks.flooring.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.href}
                    className="text-sm text-secondary-foreground/85 hover:text-primary transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h3 className="font-semibold text-secondary-foreground mb-4">Company</h3>
            <ul className="space-y-2">
              {footerLinks.company.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.href}
                    className="text-sm text-secondary-foreground/85 hover:text-primary transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Locations */}
          <div>
            <h3 className="font-semibold text-secondary-foreground mb-4">Locations</h3>
            <div className="space-y-4">
              {locations.map((location) => (
                <div key={location.name} className="text-sm">
                  <p className="font-medium text-secondary-foreground">{location.name}</p>
                  <p className="text-secondary-foreground/80">{location.address}</p>
                  <a
                    href={`tel:${location.phone.replace(/\D/g, "")}`}
                    className="text-primary hover:text-primary-light transition-colors"
                  >
                    {location.phone}
                  </a>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-secondary-foreground/10">
        <div className="container-wide py-6 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-sm text-secondary-foreground/70">
            © {currentYear} MyNewFloor.com. All rights reserved.
          </p>
          <div className="flex gap-6 text-sm">
            <a href="/privacy" className="text-secondary-foreground/70 hover:text-secondary-foreground transition-colors">
              Privacy Policy
            </a>
            <a href="/terms" className="text-secondary-foreground/70 hover:text-secondary-foreground transition-colors">
              Terms of Service
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
export { Footer };
